import passwordComplexity from "joi-password-complexity";

import Joi from "./joi";
import { convertTimeToDecimal } from "./helpers";

class FormValidationRules {
    public Joi: typeof Joi;
    constructor() {
        this.Joi = Joi;
    }
    public GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[A-Z0-9]{1}$/;

    get username() {
        return Joi.string()
            .regex(
                new RegExp(
                    /^[A-Za-z](?=[A-Za-z0-9_.]{3,31}$)[a-zA-Z0-9_]*\.?[a-zA-Z0-9_]*$/
                )
            )
            .required()
            .messages({
                "string.pattern.base":
                    "Use 4 to 32 ch aracters and start with a letter. You may use letters, numbers, underscores, and one dot (.)",
            })
            .label("Username");
    }
    set username(value) {
        this.throwSetError();
    }

    get email() {
        return Joi.string()
            .email({ tlds: { allow: false } })

            .label("Email");
    }
    set email(value) {
        this.throwSetError();
    }

    get name() {
        return Joi.string()
            .min(3)
            .pattern(/^[a-zA-Z0-9. ]*$/)
            .allow('', null)
            .messages({
                "string.pattern.base":
                    "Should contains letters and dot [.] only",
                "string.base": "This field is required",
            });
    }
    set name(value) {
        this.throwSetError();
    }
    get nameRequired() {
        return Joi.string()
            .min(3)
            .pattern(/^[a-zA-Z0-9. ]*$/)
            .required()
            .messages({
                "string.pattern.base":
                    "Should contains letters and dot [.] only",
                "string.base": "This field is required",
            });
    }
    set nameRequired(value) {
        this.throwSetError();
    }

    get password() {
        return passwordComplexity({
            min: 8,
            max: 25,
            lowerCase: 1,
            // upperCase: 1,
            // numeric: 1,
            symbol: 1,
            requirementCount: 4,
        }).label("Password");
    }
    set password(value) {
        this.throwSetError();
    }

    get confirmPassword() {
        return Joi.any().equal(Joi.ref('new_password'))
            .required()
            .label('Confirm password')
            .messages({ 'any.only': '{{#label}} does not match' })
    }

    get companyName() {
        return Joi.string()
            .pattern(/^[a-zA-Z0-9 ]*$/)
            .min(2)
            .max(100)
            .allow("")
            .label("Company Name")
            .messages({
                "string.pattern.base": "Should contains letters and numbers only",
            });
    }
    set companyName(value) {
        this.throwSetError();
    }

    get optionalField() {
        return Joi.allow("", null, 0);
    }
    set optionalField(value) {
        this.throwSetError();
    }
    get confirmationCode() {
        return Joi.string()
            .length(6)
            .pattern(/^[0-9]+$/)
            .required()
            .messages({
                "string.pattern.base": "Invalid code",
                "string.length": "Invalid code",
            })
            .label("Confirmation code");
    }
    set confirmationCode(value) {
        this.throwSetError();
    }
    set phoneNumber(value) {
        this.throwSetError();
    }
    get phoneNumber() {
        return Joi.number().min(1000000000).max(9999999999).messages({
            "number.min": "Please enter a valid phone number",
            "number.max": "Please enter a valid phone number",
        });
    }
    get postalCode() {
        return Joi.string().length(6).messages({
            "string.length": "Please enter a valid pin code",
        });
    }
    set postalCode(value) {
        this.throwSetError();
    }
    get url() {
        return Joi.string();
    }
    set url(value) {
        this.throwSetError();
    }
    get string() {
        return Joi.string().allow('', null);
    }
    set string(value) {
        this.throwSetError();
    }
    get stringRequired() {
        return Joi.string().required();
    }
    set stringRequired(value) {
        this.throwSetError();
    }
    get alphaNumeric() {
        return Joi.string().alphanum();
    }
    set alphaNumeric(value) {
        this.throwSetError();
    }
    get alphaNumericRequired() {
        return Joi.string().alphanum().required();
    }
    set alphaNumericRequired(value) {
        this.throwSetError();
    }
    get number() {
        return Joi.number();
    }
    set number(value) {
        this.throwSetError();
    }
    get positiveNumberRequired() {
        return Joi.number().positive().allow(0).required();
    }
    set positiveNumberRequired(value) {
        this.throwSetError();
    }
    get positiveNumber() {
        return Joi.number().positive().allow(0, null, '');
    }
    set positiveNumber(value) {
        this.throwSetError();
    }
    get numberRequired() {
        return Joi.number().required();
    }
    set numberRequired(value) {
        this.throwSetError();
    }
    get boolean() {
        return Joi.boolean();
    }
    set boolean(value) {
        this.throwSetError();
    }
    get booleanRequired() {
        return Joi.boolean().required();
    }
    set booleanRequired(value) {
        this.throwSetError();
    }
    get date() {
        return Joi.date().raw();
    }
    set date(value) {
        this.throwSetError();
    }
    get dateRequired() {
        return Joi.date().raw().required();
    }
    set dateRequired(value) {
        this.throwSetError();
    }
    get any() {
        return Joi.any();
    }
    set any(value) {
        this.throwSetError();
    }
    /**
     * Required rule for `select` fields, whose value is an option object rather than a string.
     *
     * `Joi.any().required()` only rejects `undefined`, so a select holding `""` (what the API
     * returns for an unset column, which reaches the form through `defaultValues`) or `null`
     * (what react-select and `setValue(name, null)` produce on clear) passes validation while
     * the control still shows its placeholder - the field looks blank but reports no error.
     */
    get selectRequired() {
        return Joi.any().required().invalid(null, "").messages({
            "any.invalid": "{{#label}} is required",
        });
    }
    set selectRequired(value) {
        this.throwSetError();
    }
    public array<T = any>() {
        return Joi.array<T>();
    }
    public arrayRequired<T = any>() {
        return Joi.array<T>().required();
    }
    public object<T = any>(schema: any) {
        return Joi.object<T>(schema);
    }
    public objectRequired<T = any>() {
        return Joi.object<T>().required();
    }
    get timeComparison() {
        return Joi.string().custom((value, helpers) => {
            const { state } = helpers;
            const orderCloseTime = state.ancestors[0]?.order_close_time;

            // If either value is empty, don't validate
            if (!value || !orderCloseTime) {
                return value;
            }

            try {
                const cronTimeDecimal = convertTimeToDecimal(value);
                const orderCloseTimeDecimal = convertTimeToDecimal(orderCloseTime);

                // Check if conversion was successful
                if (typeof cronTimeDecimal === 'number' && typeof orderCloseTimeDecimal === 'number') {
                    if (cronTimeDecimal < orderCloseTimeDecimal) {
                        return helpers.error('any.invalid');
                    }
                }
            } catch (error) {
                // If conversion fails, don't validate
                return value;
            }

            return value;
        }, 'time-comparison').messages({
            'any.invalid': 'Cron run time must be greater than or equal to order close time'
        });
    }
    set timeComparison(value) {
        this.throwSetError();
    }
    get timeComparisonRequired() {
        return Joi.string().required().custom((value, helpers) => {
            const { state } = helpers;
            const orderCloseTime = state.ancestors[0]?.order_close_time;

            // If order close time is empty, don't validate comparison
            if (!orderCloseTime) {
                return value;
            }

            try {
                const cronTimeDecimal = convertTimeToDecimal(value);
                const orderCloseTimeDecimal = convertTimeToDecimal(orderCloseTime);

                // Check if conversion was successful
                if (typeof cronTimeDecimal === 'number' && typeof orderCloseTimeDecimal === 'number') {
                    if (cronTimeDecimal < orderCloseTimeDecimal) {
                        return helpers.error('any.invalid');
                    }
                }
            } catch (error) {
                // If conversion fails, don't validate
                return value;
            }

            return value;
        }, 'time-comparison-required').messages({
            'any.invalid': 'Cron run time must be greater than or equal to order close time',
            'string.empty': 'Cron run time is required'
        });
    }
    set timeComparisonRequired(value) {
        this.throwSetError();
    }

    get timeComparisonWithRef() {
        return Joi.string().required().custom((value, helpers) => {
            const { state } = helpers;
            const orderCloseTime = state.ancestors[0]?.order_close_time;

            // If order close time is empty, don't validate comparison
            if (!orderCloseTime) {
                return value;
            }

            try {
                const cronTimeDecimal = convertTimeToDecimal(value);
                const orderCloseTimeDecimal = convertTimeToDecimal(orderCloseTime);

                // Check if conversion was successful
                if (typeof cronTimeDecimal === 'number' && typeof orderCloseTimeDecimal === 'number') {
                    if (cronTimeDecimal < orderCloseTimeDecimal) {
                        return helpers.error('any.invalid');
                    }
                }
            } catch (error) {
                // If conversion fails, don't validate
                return value;
            }

            return value;
        }, 'time-comparison-with-ref').messages({
            'any.invalid': 'Cron run time must be greater than or equal to order close time',
            'string.empty': 'Cron run time is required'
        });
    }
    set timeComparisonWithRef(value) {
        this.throwSetError();
    }
    throwSetError() {
        throw new Error("It's not possible to set this field");
    }



    get gstRequired() {
        return Joi.string().alphanum().required().max(15);
    }
    set gstRequired(value) {
        this.throwSetError();
    }
    get gst() {
        return Joi.string().empty("").max(15).pattern(this.GST_REGEX).messages({
            "string.max": "GST Number must be exactly 15 characters",
            "string.pattern.base":
                "Invalid GST Number format. Format should be: 2 digits (State Code) + 5 letters + 4 digits + 1 letter (PAN) + Entity Number (1-9 or A-Z) + Z + Checksum",
        });
    }

    // Add a more detailed GST validation method with specific error messages
    get gstDetailedValidation() {
        return Joi.string().custom((value, helpers) => {
            if (!value) {
                return helpers.message({ custom: "GST Number is required" });
            }
            // Check first 2 characters (should be numbers)
            if (!/^[0-9]{2}/.test(value)) {
                return helpers.message({ custom: "First 2 characters of GST Number must be digits (0-9)" });
            }
            // Check characters 3-7 (should be uppercase letters)
            if (!/^[0-9]{2}[A-Z]{5}/.test(value)) {
                return helpers.message({ custom: "Characters 3-7 of GST Number must be uppercase letters (A-Z)" });
            }
            // Check characters 8-11 (should be numbers)
            if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}/.test(value)) {
                return helpers.message({ custom: "Characters 8-11 of GST Number must be digits (0-9)" });
            }

            // Check character 12 (should be uppercase letter - last character of PAN)
            if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}/.test(value)) {
                return helpers.message({ custom: "Character 12 of GST Number must be an uppercase letter (A-Z)" });
            }

            // Check character 13 (should be Entity Number: 1-9 or A-Z)
            if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}/.test(value)) {
                return helpers.message({
                    custom: "Character 13 of GST Number must be a digit (1-9) or uppercase letter (A-Z)",
                });
            }

            // Check character 14 (should always be "Z")
            if (value.length >= 13 && value[13] !== "Z") {
                return helpers.message({ custom: "Character 14 of GST Number must always be 'Z'" });
            }

            // Check character 15 (should be checksum: alphanumeric but uppercase only)
            if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[A-Z0-9]{1}$/.test(value)) {
                return helpers.message({
                    custom: "Character 15 of GST Number must be an uppercase letter (A-Z) or digit (0-9)",
                });
            }

            // Check exact length
            if (value.length !== 15) {
                return helpers.message({ custom: "GST Number must be exactly 15 characters" });
            }

            // If we reach here, GST Number is valid
            return value;
        });
    }

}

const formValidationRules = new FormValidationRules();
export default formValidationRules;
